/**
 * Chunk 3 — Card-only Reveal page DESIGN MOCK (revision 2)
 *
 * Admin-only route: /admin/preview/mock-card-only
 *
 * REVISION 2 changes:
 *   1. Default state: all 8 cards COLLAPSED, only Card 1 pre-expanded.
 *   2. Hook quality bar: cards 1, 4, 5, 8 (DB) opening sentences rewritten.
 *   3. Citation chips moved out of prose body → footer chip row.
 *   4. DEEP / THIN tag removed from card visual (kept in schema).
 *   5. Capability tag chips removed from card visual (kept in schema).
 *   6. Lloyds Banking Group second mock deck added — switch via account tabs.
 *   7. 380px mobile confirmed (CSS already responsive; no breaking layout).
 *   8. Time-mode toggle filters by score order: 3 / 5 / all.
 *   9. Account-tab data hardcoded for the mock. PRODUCTION wiring note in the
 *      implementation summary.
 *
 * No production code, prompts, schemas, calibration, or data touched.
 */

import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BetaBadge } from "@/components/BetaBadge";
import { KalibLogo } from "@/components/KalibLogo";
import {
  Zap, Coffee, Search, ChevronDown, ChevronUp, ArrowUpRight, Check, Clock, X, Globe,
} from "lucide-react";

// =============================================================================
// MOCK DATA — Deutsche Bank
// =============================================================================

const MOCK_USER = { name: "Admin One" };
const MOCK_ACCOUNT_TABS = [
  { id: "deutsche_bank", name: "Deutsche Bank" },
  { id: "lloyds",        name: "Lloyds Banking Group" },
];
const MOCK_CALIBRATION = "Services & delivery · New business acquisition · Account or practice leader";

const MOCK_DECKS = {
  deutsche_bank: {
    signal_sentence:
      "Deutsche Bank's pressure to reconcile flat Q1 revenues with growth ambitions and maintain operational discipline creates a narrow opening for targeted services work packages that can demonstrate immediate impact in optimising processes or accelerating key initiatives.",
    sources: [
      { n: 1,  used: true,  outlet: "Reuters",        date: "2026-04-22", headline: "Deutsche Bank Q1 net revenue tracks flat against ambitious 2026 guidance",                                          url: "https://www.reuters.com/x/deutsche-q1" },
      { n: 2,  used: true,  outlet: "Financial Times", date: "2026-04-21", headline: "BNP Paribas signals doubled pre-tax profit ambition by 2030 after AXA IM deal",                                     url: "https://www.ft.com/content/bnp-axa" },
      { n: 3,  used: true,  outlet: "Bloomberg",      date: "2026-04-20", headline: "Deutsche Bank CFO James von Moltke to depart in 2026 — succession watched closely",                                  url: "https://www.bloomberg.com/news/von-moltke" },
      { n: 4,  used: true,  outlet: "Handelsblatt",   date: "2026-04-19", headline: "Deutsche reaffirms Global Hausbank strategy with renewed Asia and ME push",                                          url: "https://www.handelsblatt.com/db-hausbank" },
      { n: 5,  used: true,  outlet: "PRA",            date: "2026-04-18", headline: "PRA Dear CEO letter on operational resilience and capital efficiency expectations 2026",                            url: "https://www.bankofengland.co.uk/dear-ceo-2026" },
      { n: 6,  used: true,  outlet: "Reuters",        date: "2026-04-17", headline: "Deutsche Bank shareholders approve 11% dividend increase, signal scrutiny on returns",                              url: "https://www.reuters.com/db-dividend" },
      { n: 7,  used: true,  outlet: "Deutsche Bank",  date: "2026-04-15", headline: "Deutsche Bank refreshes Supervisory Board with three new members effective May 2026",                                url: "https://www.db.com/ir/board" },
      { n: 8,  used: true,  outlet: "CoinDesk",       date: "2026-04-12", headline: "Deutsche eyes regulated digital asset custody as 2026 product priority",                                            url: "https://www.coindesk.com/db-custody" },
      { n: 9,  used: false, outlet: "Bloomberg",      date: "2026-04-22", headline: "European bank Q1 earnings preview — capital ratios, IB rebound, AI capex" },
      { n: 10, used: false, outlet: "Reuters",        date: "2026-04-21", headline: "JPMorgan accelerates Germany retail rollout, sets up direct competition with local incumbents" },
      { n: 11, used: false, outlet: "FT",             date: "2026-04-21", headline: "Goldman Sachs raises 2026 IB revenue forecast on dealmaking momentum" },
      { n: 12, used: false, outlet: "Risk.net",       date: "2026-04-20", headline: "Model risk frameworks under fresh ECB scrutiny ahead of TRIM 2.0 cycle" },
      { n: 13, used: false, outlet: "Handelsblatt",   date: "2026-04-19", headline: "BaFin signals tighter expectations on third-party tech risk reporting" },
      { n: 14, used: false, outlet: "Reuters",        date: "2026-04-19", headline: "ECB raises mild concerns over corporate lending standard drift" },
      { n: 15, used: false, outlet: "FT",             date: "2026-04-18", headline: "European wealth managers — assets up, margins under quiet pressure" },
      { n: 16, used: false, outlet: "Bloomberg",      date: "2026-04-18", headline: "Investment banks ramp generative AI for sales-side coverage tooling" },
      { n: 17, used: false, outlet: "Risk.net",       date: "2026-04-17", headline: "Operational resilience playbooks evolve — recovery time objectives tighten" },
      { n: 18, used: false, outlet: "Reuters",        date: "2026-04-17", headline: "DWS reports Q1 inflows against challenging market backdrop" },
      { n: 19, used: false, outlet: "FT",             date: "2026-04-16", headline: "BNP Paribas CIB technology budget said to be re-prioritised toward AI deployment" },
      { n: 20, used: false, outlet: "Bloomberg",      date: "2026-04-16", headline: "Postbank service complaints highlighted in Bundestag committee" },
      { n: 21, used: false, outlet: "American Banker", date: "2026-04-15", headline: "European bank IT outsourcing patterns shifting toward selective in-housing" },
      { n: 22, used: false, outlet: "Reuters",        date: "2026-04-14", headline: "Deutsche Bank confirms cost discipline themes for 2026" },
      { n: 23, used: false, outlet: "FT",             date: "2026-04-13", headline: "European banks lag US peers on capital markets technology refresh" },
      { n: 24, used: false, outlet: "Bloomberg",      date: "2026-04-12", headline: "Buy-side custody fees compress further; tokenisation adds new vector" },
      { n: 25, used: false, outlet: "Handelsblatt",   date: "2026-04-11", headline: "Deutsche Bank highlights Asia-Pacific growth markets at investor day" },
    ],
    cards: [
      {
        rank: 1, depth: "deep", cross_connected: true,
        title: "Use BNP's AI ambition as a DWS / wealth pressure-test",
        primary_signal_anchor: "Deutsche reaffirms Global Hausbank strategy with renewed Asia and ME push",
        primary_anchor_outlet: "Handelsblatt", primary_anchor_date: "2026-04-19",
        capability_tags: ["AI_DATA", "INTEGRATION_ARCHITECTURE"],
        cited_sources: [4, 2, 18],
        body_markdown:
`BNP Paribas just told the market it intends to double pre-tax profits by 2030 [2]. That does not change Deutsche Bank's strategy. It does sharpen the questions worth asking inside it [4].

Deutsche Bank is publicly leaning into the Global Hausbank strategy, with wealth management and corporate banking expansion in Southeast Asia and the Middle East part of the growth picture. BNP is putting a sharper marker down in asset management — global expansion, AI integration, and the AXA IM acquisition behind the doubled-profit ambition. Banks are not schoolchildren copying homework, even if some strategy decks make it look that way. But it does create a useful commercial comparison.

If you touch DWS, wealth, data, client platforms, AI delivery, or regional execution, the better question is not whether AI is "relevant." It is whether AI can help Deutsche Bank improve distribution, advisor productivity, client insight, onboarding, or operating leverage without adding yet another layer of complexity.

Your move may be to test which wedge is most credible:

1. For wealth or DWS conversations [18], frame AI as a productivity and client-insight lever, not a standalone programme.
2. For regional expansion, ask whether local execution, data flows, adviser tooling, or client onboarding are the real constraint.
3. For existing relationships, use BNP as a benchmark question: where could Deutsche Bank face pressure to show sharper asset-management or wealth productivity?

This is not about saying "BNP did AI, so Deutsche must." It is about using competitor ambition to ask better account questions.`,
      },
      {
        rank: 2, depth: "deep",
        title: "Treat the von Moltke departure as a CFO political opening, not an emergency",
        primary_signal_anchor: "Deutsche Bank CFO James von Moltke to depart in 2026 — succession watched closely",
        primary_anchor_outlet: "Bloomberg", primary_anchor_date: "2026-04-20",
        capability_tags: ["MANAGED_SERVICES", "RISK_COMPLIANCE"],
        cited_sources: [3, 6, 7, 22],
        body_markdown:
`The CFO succession at Deutsche Bank [3] is one of those signals that generates more LinkedIn theatre than commercial action. The mistake is to chase the new name. The smarter play is to read what the change creates inside the bank.

A new CFO inherits the existing 2026 cost discipline narrative [22], the dividend-return scrutiny coming from shareholders [6], and the supervisory board refresh [7] — all at once. That is a lot of optics in one seat. Several decisions on the bank's operating model that were "decided" under von Moltke are quietly back open.

If you serve Deutsche around finance transformation, control tooling, regulatory reporting, or run-the-bank work, the conversation worth preparing for is not "let me re-pitch what we already do." It is "where does this CFO inherit a problem they didn't create, and where can we make their first 100 days easier?"

## Why this matters

A CFO transition is not a deal trigger. It is a political reset. The vendors who help the new CFO look prepared, controlled, and unblocked early are the ones still in the room twelve months later.`,
      },
      {
        rank: 3, depth: "thin",
        title: "Read the Supervisory Board refresh as shareholder-return scrutiny",
        primary_signal_anchor: "Deutsche Bank refreshes Supervisory Board with three new members effective May 2026",
        primary_anchor_outlet: "Deutsche Bank", primary_anchor_date: "2026-04-15",
        capability_tags: ["RISK_COMPLIANCE"],
        cited_sources: [7, 6],
        body_markdown:
`The Supervisory Board refresh [7] arriving in the same cycle as an 11% dividend increase [6] is not coincidence. New independent directors will spend their first two meetings asking exactly the questions large shareholders have been quietly asking for a year — capital efficiency, RoTE trajectory, cost-to-income persistence.

If your work touches anything those questions cascade into — finance, risk, controls, programme governance — the question is which of your in-flight programmes can be re-narrated as a contributor to the answer, before someone else narrates them as a contributor to the cost line.`,
      },
      {
        rank: 4, depth: "deep", cross_connected: true,
        title: "Position around the PRA capital-efficiency expectation as a delivery cadence shift",
        primary_signal_anchor: "PRA Dear CEO letter on operational resilience and capital efficiency expectations 2026",
        primary_anchor_outlet: "PRA", primary_anchor_date: "2026-04-18",
        capability_tags: ["RISK_COMPLIANCE", "CLOUD_INFRA"],
        cited_sources: [5, 12, 17],
        body_markdown:
`Most large bank programmes are still running on a 2024 evidence cadence [5]. Regulators in 2026 are quietly asking for monthly. The gap between the two is now a delivery question, not a policy one — and the people running existing engagements may be the last to know it has changed.

ECB scrutiny on model risk [12] reinforces the direction. Operational resilience playbooks across the sector are tightening recovery-time objectives [17]. None of this is Deutsche-specific. All of it changes what "good" looks like in 2026 conversations with the bank.

The question that surfaces inside large engagements right now is whether existing delivery cadence — quarterly milestones, annual programme reviews, slow controls upgrades — can keep pace with a regulatory environment that is asking for faster, more demonstrable resilience evidence.

If you are running, or trying to win, work that touches risk reporting, model governance, controls automation, or operational resilience tooling at Deutsche, your move may be to test three angles:

- For existing programmes: can you propose a tighter evidence cadence (monthly, not quarterly) without renegotiating scope?
- For new conversations: lead with the PRA expectation as the framing, not a generic "we do risk".
- For sponsor conversations: ask whether their internal cadence is matching what regulators are about to expect, or whether they are still on a 2024 rhythm.

The PRA letter is useful here as a pressure signal, not a prediction.`,
      },
      {
        rank: 5, depth: "thin",
        title: "Watch the digital asset custody focus as a positioning lens, not a project",
        primary_signal_anchor: "Deutsche eyes regulated digital asset custody as 2026 product priority",
        primary_anchor_outlet: "CoinDesk", primary_anchor_date: "2026-04-12",
        capability_tags: ["INTEGRATION_ARCHITECTURE", "RISK_COMPLIANCE"],
        cited_sources: [8, 24],
        body_markdown:
`Custody is a brutal margin business with shrinking fees [24] and compounding control complexity. Deutsche entering it in 2026 [8] is interesting precisely because nobody is winning easy money there.

Treat it as a positioning lens for service offerings — where credibility on regulatory engagement, controls design, and integration with existing trade lifecycle plumbing matters more than custody-specific expertise. Buy-side fee compression around custody makes the economics tighter, not looser, for a new entrant.

The question worth landing this quarter: which of your existing capabilities can be re-presented as adjacent to this priority, without overclaiming custody experience?`,
      },
      {
        rank: 6, depth: "thin",
        title: "Read the dividend increase as a margin-discipline signal",
        primary_signal_anchor: "Deutsche Bank shareholders approve 11% dividend increase, signal scrutiny on returns",
        primary_anchor_outlet: "Reuters", primary_anchor_date: "2026-04-17",
        capability_tags: ["MANAGED_SERVICES"],
        cited_sources: [6, 1],
        body_markdown:
`An 11% dividend increase [6] in a year of flat Q1 revenue [1] is a deliberately public commitment to shareholder return. It quietly raises the bar on every line item the bank is paying for.

Not a deal-trigger. But the question worth asking inside live commercial conversations: can your current scope be defended as a contributor to capital efficiency, and not as a fixed cost?`,
      },
      {
        rank: 7, depth: "thin",
        title: "Take the cost-discipline narrative seriously in pricing conversations",
        primary_signal_anchor: "Deutsche Bank confirms cost discipline themes for 2026",
        primary_anchor_outlet: "Reuters", primary_anchor_date: "2026-04-14",
        capability_tags: ["MANAGED_SERVICES", "ENTERPRISE_SAAS"],
        cited_sources: [22, 1],
        body_markdown:
`The cost-discipline confirmation [22] paired with flat Q1 revenue framing [1] makes 2026 pricing conversations harder than 2025's. List-price increases will be challenged, even on legacy SoWs.

The question this week: what reframing of your existing pricing as outcome-aligned (rather than capacity-aligned) is credible, given what the account actually buys from you?`,
      },
      {
        rank: 8, depth: "deep", cross_connected: true,
        title: "Use Q1 results timing to prepare a flexible delivery doorway",
        primary_signal_anchor: "Deutsche Bank Q1 net revenue tracks flat against ambitious 2026 guidance",
        primary_anchor_outlet: "Reuters", primary_anchor_date: "2026-04-22",
        capability_tags: ["MANAGED_SERVICES", "INTEGRATION_ARCHITECTURE"],
        cited_sources: [1, 11, 10, 4, 25],
        body_markdown:
`Most account teams will walk into the post-Q1 window [1] with one rehearsed story. The same proposition can sound either disciplined or tone-deaf depending on the number on the screen. Bring two.

Goldman raising its 2026 IB revenue forecast [11] and the JPMorgan retail expansion in Germany [10] add competitive pressure to the framing — even where the bank doesn't name them publicly. That connection matters because the same delivery proposition can sound very different depending on the post-results mood. If the numbers are softer than the guidance promised, scrutiny rises sharply on existing scope. If the bank lands closer to plan, growth ambitions [4] get oxygen and capacity conversations open up.

If you are protecting in-flight engagements at Deutsche, this week's preparation is less about predicting and more about pre-arming both narratives:

- A "discipline mode" version that ties your work to capital efficiency, run-cost transparency, and demonstrable cost-out per quarter.
- A "growth mode" version that ties your work to acceleration of the Hausbank ambition, AI-readiness, or Asia-Pacific delivery scaling [25].

The work may be the same. The doorway changes — and in large accounts, the doorway is often the difference between useful timing and polite irrelevance.`,
      },
    ],
  },

  // ==========================================================================
  // MOCK DATA — Lloyds Banking Group
  // ==========================================================================
  lloyds: {
    signal_sentence:
      "Three CEO-level appointments in three months and a publicly-launched AI Academy give Lloyds Banking Group an unusually tight execution-readiness window heading into Q1 results — and the partners best-placed to land in that window will be the ones who turn appointment news into a delivery hypothesis before the inboxes fill up.",
    sources: [
      { n: 1, used: true,  outlet: "Lloyds Banking Group", date: "2026-04-22", headline: "Sameer Gupta appointed Chief Data and Analytics Officer, joining Group Executive Committee", url: "https://www.lloydsbankinggroup.com/news/gupta-cdao" },
      { n: 2, used: true,  outlet: "Reuters",              date: "2026-04-19", headline: "Amanda Murphy named CEO of Business and Commercial Banking at Lloyds",                       url: "https://www.reuters.com/lbg-bcb" },
      { n: 3, used: true,  outlet: "FT",                   date: "2026-04-17", headline: "John Langley joins Lloyds as CEO of Corporate and Institutional Banking",                    url: "https://www.ft.com/lbg-cib" },
      { n: 4, used: true,  outlet: "Bloomberg",            date: "2026-04-15", headline: "Lloyds launches AI Academy to scale colleague capability across 60,000 employees",           url: "https://www.bloomberg.com/lbg-ai-academy" },
      { n: 5, used: true,  outlet: "Lloyds Banking Group", date: "2026-04-22", headline: "Lloyds Q1 2026 results scheduled for late April release",                                    url: "https://www.lloydsbankinggroup.com/ir/q1-2026" },
      { n: 6, used: true,  outlet: "Risk.net",             date: "2026-04-18", headline: "Model risk and AI explainability move up the PRA supervisory agenda",                       url: "https://www.risk.net/pra-model-risk" },
      { n: 7, used: true,  outlet: "FT",                   date: "2026-04-16", headline: "UK SME lending grows for the first time in six quarters; sentiment uneven",                 url: "https://www.ft.com/uk-sme-lending" },
      { n: 8, used: true,  outlet: "Bloomberg",            date: "2026-04-14", headline: "UK banking sector AI capex up 38% year-on-year, Bloomberg Intelligence reports",            url: "https://www.bloomberg.com/uk-bank-ai" },
      { n: 9, used: false, outlet: "Reuters",              date: "2026-04-22", headline: "UK retail banks face new operational resilience cadence post-Dear CEO letter" },
      { n: 10, used: false, outlet: "American Banker",     date: "2026-04-21", headline: "European retail banks accelerate generative AI deployment in customer servicing" },
      { n: 11, used: false, outlet: "FT",                  date: "2026-04-20", headline: "Lloyds digital active users continue outperformance versus high-street peers" },
      { n: 12, used: false, outlet: "Bloomberg",           date: "2026-04-19", headline: "Consumer Duty implementation pressures persist across UK product governance teams" },
      { n: 13, used: false, outlet: "Reuters",             date: "2026-04-18", headline: "Halifax mortgage approvals tick up amid market re-pricing dynamic" },
      { n: 14, used: false, outlet: "Risk.net",            date: "2026-04-17", headline: "AI governance frameworks under fresh FCA scrutiny ahead of model inventories deadline" },
      { n: 15, used: false, outlet: "FT",                  date: "2026-04-16", headline: "UK challenger banks pull back on commercial banking ambitions amid funding cost rise" },
      { n: 16, used: false, outlet: "Bloomberg",           date: "2026-04-15", headline: "European bank tech-spend reallocates toward AI integration, away from refresh" },
      { n: 17, used: false, outlet: "Reuters",             date: "2026-04-14", headline: "PRA flags concentration risk in third-party cloud arrangements" },
      { n: 18, used: false, outlet: "FT",                  date: "2026-04-12", headline: "UK SME confidence diverges sharply between firms above and below £5m turnover" },
      { n: 19, used: false, outlet: "Bloomberg",           date: "2026-04-11", headline: "Open banking activity hits new high as PSD3 finalisation approaches" },
      { n: 20, used: false, outlet: "American Banker",     date: "2026-04-10", headline: "European banks adopt agentic AI for relationship-manager workflows in commercial banking" },
      { n: 21, used: false, outlet: "Risk.net",            date: "2026-04-09", headline: "Operational resilience evidence requirements tighten across UK supervised firms" },
    ],
    cards: [
      {
        rank: 1, depth: "deep", cross_connected: true,
        title: "Read Sameer Gupta's CDAO seat as the moment Lloyds' AI ambition gets a single owner",
        primary_signal_anchor: "Sameer Gupta appointed Chief Data and Analytics Officer, joining Group Executive Committee",
        primary_anchor_outlet: "Lloyds Banking Group", primary_anchor_date: "2026-04-22",
        capability_tags: ["AI_DATA", "INTEGRATION_ARCHITECTURE"],
        cited_sources: [1, 4, 8, 16],
        body_markdown:
`A CDAO appointment in 2026 is no longer a data role. It is the seat where AI ambition either compounds or quietly disappears into governance [1]. Lloyds just put Sameer Gupta in that seat — and paired the move with a public AI Academy launch [4], which is the shape of an organisation that wants accountability for the strategy, not just enthusiasm.

UK banking sector AI capex is up 38% year-on-year [8], and across Europe, tech budgets are reallocating away from refresh and toward AI integration [16]. That is the macro picture Gupta walks into. The micro picture is which of the existing in-flight engagements at Lloyds have a clean line of sight to his agenda — and which were built for the previous CDO logic.

If you touch data platforms, AI delivery, MLOps, model governance, or the integration architecture beneath any of those, your move may be to:

1. For existing programmes, ask whether the way they currently report into the data function will survive a CDAO who needs to demonstrate compounding value across the bank, not within one division.
2. For new conversations, frame the proposition around helping the AI Academy translate capability into measurable workflow change in the first six months — Academies need landing zones.
3. For sponsor conversations, surface where AI ambition is most likely to hit governance friction first (model approval cadence, evidence cadence, controls integration) and pre-empt it.

A CDAO with executive committee seat-power changes the geometry of every AI conversation in the bank. Worth re-reading your account map this week against that geometry.`,
      },
      {
        rank: 2, depth: "deep", cross_connected: true,
        title: "Treat Amanda Murphy's BCB move as a frontline-growth question, not a leadership headline",
        primary_signal_anchor: "Amanda Murphy named CEO of Business and Commercial Banking at Lloyds",
        primary_anchor_outlet: "Reuters", primary_anchor_date: "2026-04-19",
        capability_tags: ["MANAGED_SERVICES", "AI_DATA"],
        cited_sources: [2, 7, 18, 20],
        body_markdown:
`UK SME lending is growing again [7], but business confidence is uneven across firm sizes [18]. Amanda Murphy walks into BCB owning both the upside and the segmentation problem nobody has solved [2].

That connection matters because it points to a segment where growth may be available, but not evenly distributed or easy to serve. For Lloyds, the question may not simply be "how do we lend more?" — it may be "how do we grow responsibly without asking relationship managers, risk teams, and operations to absorb all the complexity manually?"

Across European peers, agentic AI is starting to land in relationship-manager workflows in commercial banking [20] — the early adopters are using it for credit-paper preparation, segment-level early-warning indicators, and faster opportunity qualification.

If you work around commercial banking, data, workflow, credit operations, relationship-manager tooling, or AI-assisted insight, avoid the vague opener:

- "We can support your growth agenda."

A stronger conversation would help Lloyds see where better segmentation, faster credit journeys, earlier risk visibility, or sharper frontline prompts could improve both client service and bank economics.

## Why this matters

Growth in BCB is not just a revenue opportunity. It is a judgement problem at the edge of the bank — and a new CEO will spend the first ninety days deciding which judgement problems she wants to inherit and which ones she wants to redesign.`,
      },
      {
        rank: 3, depth: "thin",
        title: "Read the John Langley CIB appointment as a signal that the next twelve months are about execution",
        primary_signal_anchor: "John Langley joins Lloyds as CEO of Corporate and Institutional Banking",
        primary_anchor_outlet: "FT", primary_anchor_date: "2026-04-17",
        capability_tags: ["MANAGED_SERVICES"],
        cited_sources: [3, 1, 2],
        body_markdown:
`Three CEO-level appointments in one quarter is not an HR cycle [1, 2, 3]. It is a public commitment that the next twelve months will be about execution, not strategy. Worth reading the calendar that way.

For service vendors, this means the conversation that lands is about delivery credibility, sequencing, and partner reliability — not about strategic alignment decks. New CEOs do not need to be told what their strategy is. They need to be helped to land it without their first quarter being a tactical scramble.

What part of your existing scope is most genuinely defensible as "this gets harder if we are not in the room"?`,
      },
      {
        rank: 4, depth: "deep", cross_connected: true,
        title: "Use the AI Academy launch to start the model-risk conversation early",
        primary_signal_anchor: "Lloyds launches AI Academy to scale colleague capability across 60,000 employees",
        primary_anchor_outlet: "Bloomberg", primary_anchor_date: "2026-04-15",
        capability_tags: ["AI_DATA", "RISK_COMPLIANCE"],
        cited_sources: [4, 6, 14, 21],
        body_markdown:
`Lloyds is building AI capability internally [4] just as regulators are tightening expectations on model risk and explainability [6]. The Academy is the visible thing. The control framework around it is the harder build — and the harder sell.

PRA expectations on model inventories, validation cadence, and explainability are moving up the supervisory agenda. The FCA is asking the same questions from a different angle [14], and operational resilience evidence requirements continue to tighten across UK supervised firms [21].

That gives any vendor working in the AI delivery, model governance, or operational-controls space a specific framing opportunity this quarter:

- For existing programmes, can you propose adding a "model evidence-readiness" workstream that runs alongside the Academy rollout — not after it?
- For new conversations, frame the offer as helping Lloyds keep the AI capability narrative and the AI control narrative on the same page through 2026, not handed off between functions.
- For sponsor conversations, ask whether the Academy's training cadence is reflected in the model approval cadence — most banks are still mismatched there, and it is the kind of mismatch that turns into a regulatory finding eighteen months later.

The Academy without the controls is a press release. The controls without the Academy is friction. The vendors most credible in 2026 will be the ones who can hold both at once — and explain why holding both is cheaper than holding either separately.`,
      },
      {
        rank: 5, depth: "thin",
        title: "Read Q1 results into the existing-engagement defence narrative early",
        primary_signal_anchor: "Lloyds Q1 2026 results scheduled for late April release",
        primary_anchor_outlet: "Lloyds Banking Group", primary_anchor_date: "2026-04-22",
        capability_tags: ["MANAGED_SERVICES"],
        cited_sources: [5, 9],
        body_markdown:
`Q1 results land into a market that has already decided UK banks are either disciplined or distracted [5]. Lloyds will be read against both labels — sometimes in the same conversation, sometimes by the same analyst.

If you are running existing scope at LBG, the conversation worth pre-arming is the cost-discipline framing: which line items in your current engagement contribute to operational resilience [9] or capital efficiency narratives the bank will want to walk into Q2 already telling? The vendors who help the bank narrate those lines first are the ones still in the room when scrutiny tightens.`,
      },
      {
        rank: 6, depth: "thin",
        title: "Lloyds' digital metrics quietly outperform peers — that is now the bar",
        primary_signal_anchor: "Lloyds digital active users continue outperformance versus high-street peers",
        primary_anchor_outlet: "FT", primary_anchor_date: "2026-04-20",
        capability_tags: ["INTEGRATION_ARCHITECTURE", "MANAGED_SERVICES"],
        cited_sources: [11],
        body_markdown:
`Lloyds' digital metrics quietly outperform peers, and that fact is now the bar for what "good" looks like on the high street [11]. Falling back is harder than staying ahead, but staying ahead requires the un-glamorous plumbing — observability, error rates, journey reliability, cross-channel handoffs.

The conversation worth landing this quarter: which of your existing engagements is contributing to the plumbing story, and how visibly is that being told inside the bank?`,
      },
      {
        rank: 7, depth: "thin",
        title: "Consumer Duty maturity is uneven — that is a delivery question now",
        primary_signal_anchor: "Consumer Duty implementation pressures persist across UK product governance teams",
        primary_anchor_outlet: "Bloomberg", primary_anchor_date: "2026-04-19",
        capability_tags: ["RISK_COMPLIANCE"],
        cited_sources: [12],
        body_markdown:
`The phrase "consumer duty" is now five quarters old, but the operational implications are still landing inside servicing, complaints, and product governance teams [12]. The maturity is uneven — some product lines have it codified into journey design, others are still doing it manually quarter-by-quarter.

For services vendors with any visibility into customer journeys, complaints, or product governance workflows, the question is which of those areas is most exposed to a maturity audit in the second half of 2026 — and whether your current scope can be re-narrated as helping close that gap.`,
      },
      {
        rank: 8, depth: "deep", cross_connected: true,
        title: "Three appointments and an Academy launch — this is a window, not a season",
        primary_signal_anchor: "UK banking sector AI capex up 38% year-on-year, Bloomberg Intelligence reports",
        primary_anchor_outlet: "Bloomberg", primary_anchor_date: "2026-04-14",
        capability_tags: ["MANAGED_SERVICES", "AI_DATA"],
        cited_sources: [1, 2, 3, 4, 8, 16],
        body_markdown:
`Three new senior appointments in three months [1, 2, 3], a publicly-launched AI Academy [4], and a UK banking sector adding 38% to AI capex year-on-year [8]. Lloyds is repositioning faster than its peers — and fewer external partners are paying attention than should be.

That connection matters because the partners most credible at LBG in 2026 will not be the ones with the best capability decks. They will be the ones who turned this quarter's appointment news into a delivery hypothesis before the new CEOs' inboxes filled up. Across European peers, AI capex is being reallocated toward integration rather than refresh [16] — which means the partners who help with integration sequencing and cross-functional landing are the ones whose work compounds.

If you are running engagements at LBG that touch any of: data, AI delivery, commercial banking workflow, model governance, or change-programme execution, your move this week may be to:

- For each of the three new CEOs' surfaces, draft one-page hypothesis notes — not pitches — on what their first 90 days could look like with sharper sequencing help.
- For the AI Academy, surface where your current work creates landing zones for Academy-trained colleagues to do something concrete, not theoretical.
- For your in-flight programmes, re-narrate the year's roadmap into the new leadership team's language before the new leadership team writes it themselves.

The window is real. It is also short. The partners best-placed to land in it will be the ones who treat appointment news as a delivery question, not a relationship question.`,
      },
    ],
  },
};

// =============================================================================
// HELPERS
// =============================================================================

const TIME_MODES = [
  { id: "between",  label: "Between meetings", sub: "5 min",        icon: Zap,    cardLimit: 3 },
  { id: "coffee",   label: "Coffee break",     sub: "15 min",       icon: Coffee, cardLimit: 5 },
  { id: "deepdive", label: "Deep dive",        sub: "Full picture", icon: Search, cardLimit: 999 },
];

/** Strip [N] tokens from markdown. Used to clean prose for the new "chips at
 *  the end of card" presentation — citations no longer interrupt reading. */
function stripCitationTokens(markdown) {
  return markdown.replace(/\s*\[\d+\]\s*/g, " ").replace(/\s+/g, " ").replace(/\s+([.,;:?!])/g, "$1");
}

/** Render markdown without citation chips. Same renderer as v1, minus chip handling. */
function CardBody({ markdown }) {
  const cleaned = markdown
    .split("\n\n")
    .map((p) => stripCitationTokens(p).trim())
    .filter(Boolean);
  return (
    <div style={{ fontSize: "15px", lineHeight: 1.65 }} className="text-zinc-900 space-y-3">
      {cleaned.map((p, i) => {
        if (p.startsWith("## ")) {
          return (
            <h3 key={i} className="text-[12px] font-semibold uppercase tracking-widest text-amber-800 mt-4 mb-1">
              {p.slice(3)}
            </h3>
          );
        }
        const lines = p.split("\n").map((l) => l.trim()).filter(Boolean);
        const isOl = lines.length > 1 && lines.every((l) => /^\d+\.\s+/.test(l));
        const isUl = lines.length > 1 && lines.every((l) => /^-\s+/.test(l));
        if (isOl) {
          return (
            <ol key={i} className="list-decimal ml-5 space-y-1.5">
              {lines.map((l, j) => <li key={j}>{l.replace(/^\d+\.\s+/, "")}</li>)}
            </ol>
          );
        }
        if (isUl) {
          return (
            <ul key={i} className="list-disc ml-5 space-y-1.5">
              {lines.map((l, j) => <li key={j}>{l.replace(/^-\s+/, "")}</li>)}
            </ul>
          );
        }
        return <p key={i}>{p}</p>;
      })}
    </div>
  );
}

function CitationChipFooter({ ns, sources }) {
  if (!ns?.length) return null;
  return (
    <div className="mt-4 pt-3 border-t border-zinc-100 flex items-center gap-2 flex-wrap" data-testid="citation-chip-footer">
      <span className="text-[11px] uppercase tracking-widest text-zinc-600 mr-1">Citations</span>
      {ns.map((n) => {
        const src = sources.find((s) => s.n === n);
        const tooltip = src ? `${src.headline} — ${src.outlet}` : "";
        return (
          <a
            key={n}
            href={`#source-${n}`}
            title={tooltip}
            className="inline-flex items-center justify-center px-2.5 py-1 rounded-sm bg-amber-50 text-amber-800 hover:bg-amber-100 hover:text-amber-900 border border-amber-200 transition-colors no-underline"
            style={{ fontSize: "11px", fontWeight: 600, minHeight: "32px", minWidth: "44px", textAlign: "center" }}
            data-testid={`citation-chip-${n}`}
            onClick={(e) => {
              e.preventDefault();
              const ev = new CustomEvent("mock:open-sources", { detail: { n } });
              window.dispatchEvent(ev);
            }}
          >
            [{n}]
          </a>
        );
      })}
    </div>
  );
}

function FeedbackChips({ rank, currentState, onChoose }) {
  const cls = (state) =>
    `flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[12px] font-medium transition-colors min-h-[44px] ${
      currentState === state
        ? state === "relevant"
          ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
          : state === "not_this_week"
            ? "bg-amber-50 text-amber-800 border border-amber-300"
            : "bg-zinc-100 text-zinc-700 border border-zinc-300"
        : "bg-white text-zinc-700 border border-zinc-200 hover:border-zinc-400"
    }`;
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid={`mock-card-${rank}-feedback`}>
      <button onClick={() => onChoose("relevant")} className={cls("relevant")} data-testid={`mock-card-${rank}-fb-relevant`}>
        <Check className="w-3 h-3" /> Relevant
      </button>
      <button onClick={() => onChoose("not_this_week")} className={cls("not_this_week")} data-testid={`mock-card-${rank}-fb-not-this-week`}>
        <Clock className="w-3 h-3" /> Not this week
      </button>
      <button onClick={() => onChoose("not_relevant")} className={cls("not_relevant")} data-testid={`mock-card-${rank}-fb-not-relevant`}>
        <X className="w-3 h-3" /> Not relevant
      </button>
    </div>
  );
}

function ActionCard({ card, expanded, onToggle, feedback, onFeedback, sources }) {
  // Preview hook: first ~25 words of the body, citations stripped, line-clamped to 3.
  const previewText = useMemo(() => {
    const firstPara = card.body_markdown.split("\n\n")[0] || "";
    return stripCitationTokens(firstPara).replace(/\*\*/g, "");
  }, [card.body_markdown]);

  return (
    <div
      id={`action-${card.rank}`}
      className="border-l-4 border-l-amber-700 border border-zinc-200 bg-white p-5 scroll-mt-24"
      data-testid={`mock-card-${card.rank}`}
    >
      <div className="flex items-start gap-4">
        <span
          className="flex-shrink-0 inline-flex items-center justify-center rounded-full bg-zinc-950 text-white font-semibold mt-0.5"
          style={{ width: "32px", height: "32px", fontSize: "15px", lineHeight: 1 }}
        >
          {card.rank}
        </span>
        <div className="flex-1 min-w-0">
          {/* Title — globe marker rendered inline only when card is collapsed
              (item 10: cross-connection visual signal). Once expanded, the
              cross-connection is self-evident in the prose; marker hides. */}
          <p style={{ fontSize: "17px", fontWeight: 600, color: "#111111", lineHeight: 1.4 }} data-testid={`mock-card-${card.rank}-title`}>
            {!expanded && card.cross_connected && (
              <Globe
                className="inline-block align-[-2px] mr-1.5 text-zinc-700"
                style={{ width: "15px", height: "15px" }}
                strokeWidth={1.75}
                aria-hidden="true"
                data-testid={`mock-card-${card.rank}-cross-connected-icon`}
              />
            )}
            {card.title}
          </p>

          {/* Anchor + source line */}
          <div className="mt-1">
            <p style={{ fontSize: "12px", fontWeight: 500, color: "#B45309" }}>{card.primary_signal_anchor}</p>
            <p className="text-[11px] text-zinc-700 mt-0.5">
              <ArrowUpRight className="inline w-3 h-3 mr-0.5 text-zinc-500" strokeWidth={1.5} />
              {card.primary_anchor_outlet} · {card.primary_anchor_date}
            </p>
          </div>

          {/* Body — collapsed shows hook only, expanded shows full prose */}
          {expanded ? (
            <div className="mt-3" data-testid={`mock-card-${card.rank}-body-expanded`}>
              <CardBody markdown={card.body_markdown} />
              <CitationChipFooter ns={card.cited_sources} sources={sources} />
            </div>
          ) : (
            <p
              className="mt-3 text-zinc-900 line-clamp-3"
              style={{ fontSize: "15px", lineHeight: 1.55 }}
              data-testid={`mock-card-${card.rank}-body-preview`}
            >
              {previewText}
            </p>
          )}

          {/* Toggle */}
          <div className="mt-3 flex items-center justify-end">
            <button
              onClick={onToggle}
              className="text-[13px] font-medium text-amber-700 hover:text-amber-800 transition-colors px-3 py-2 -mr-3 -my-2 min-h-[44px]"
              data-testid={`mock-card-${card.rank}-toggle`}
            >
              {expanded ? "Show less" : "More..."}
            </button>
          </div>

          {/* Feedback (expanded only) */}
          {expanded && (
            <div className="mt-3 pt-3 border-t border-zinc-100">
              <FeedbackChips rank={card.rank} currentState={feedback} onChoose={onFeedback} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// PAGE
// =============================================================================

export default function MockCardOnlyPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [timeMode, setTimeMode] = useState("deepdive");
  const [activeAccount, setActiveAccount] = useState("deutsche_bank");
  // Default state per spec: ALL collapsed, only Card 1 pre-expanded.
  const [expandedRanks, setExpandedRanks] = useState({ 1: true });
  const [feedback, setFeedback] = useState({});
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // Reset expanded/feedback state when the user switches account tab.
  useEffect(() => {
    setExpandedRanks({ 1: true });
    setFeedback({});
    setSourcesOpen(false);
  }, [activeAccount]);

  // Admin gate
  useEffect(() => {
    if (user === null) { navigate("/signin"); return; }
    if (user && !user.is_admin) navigate("/intelligence");
  }, [user, navigate]);

  // Citation chip clicks: open sources, scroll to anchor.
  useEffect(() => {
    const handler = (e) => {
      const n = e.detail?.n;
      setSourcesOpen(true);
      setTimeout(() => {
        const target = document.getElementById(`source-${n}`);
        if (target) target.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 80);
    };
    window.addEventListener("mock:open-sources", handler);
    return () => window.removeEventListener("mock:open-sources", handler);
  }, []);

  const deck = MOCK_DECKS[activeAccount];
  const accountName = MOCK_ACCOUNT_TABS.find((t) => t.id === activeAccount)?.name || "";
  const cardLimit = TIME_MODES.find((m) => m.id === timeMode)?.cardLimit ?? 999;
  const visibleCards = useMemo(() => deck.cards.slice(0, cardLimit), [deck, cardLimit]);

  const handleToggleCard = (card) => {
    const isOpening = !expandedRanks[card.rank];
    setExpandedRanks((prev) => ({ ...prev, [card.rank]: !prev[card.rank] }));
    if (isOpening) {
      // eslint-disable-next-line no-console
      console.log("telemetry:card_expand", {
        card_rank: card.rank,
        card_title: card.title,
        capability_tags: card.capability_tags,
        depth: card.depth,
        account: activeAccount,
      });
      setTimeout(() => {
        const el = document.getElementById(`action-${card.rank}`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 60);
    }
  };

  const handleFeedback = (rank, state) => {
    setFeedback((prev) => {
      const next = { ...prev, [rank]: prev[rank] === state ? undefined : state };
      // eslint-disable-next-line no-console
      console.log("telemetry:card_feedback", {
        card_rank: rank,
        new_state: next[rank] || null,
        prev_state: prev[rank] || null,
        account: activeAccount,
      });
      return next;
    });
  };

  if (!user?.is_admin) return null;

  const usedSources = deck.sources.filter((s) => s.used);
  const otherSources = deck.sources.filter((s) => !s.used);

  return (
    <div className="min-h-screen bg-zinc-100" data-testid="mock-card-only-page">
      {/* Trial banner */}
      <div className="bg-amber-700 text-white text-center text-[13px] py-1.5 px-4">
        14-day trial · DESIGN MOCK — not the live Reveal
      </div>

      {/* Header */}
      <header className="bg-white border-b border-zinc-200 sticky top-0 z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Link to="/" className="block" data-testid="mock-card-logo-link" aria-label="Kalib AI home">
                <KalibLogo variant="light" height={28} testId="mock-card-header-logo" />
              </Link>
              <BetaBadge />
            </div>
            <Link to="/admin/accounts" className="text-[13px] text-amber-700 hover:text-amber-800">← Admin</Link>
          </div>
          <h1 className="text-[20px] sm:text-2xl font-medium tracking-tight text-zinc-950">
            Good morning, {MOCK_USER.name}. Here is your {accountName} Reveal.
          </h1>

          {/* Time mode toggle */}
          <div className="mt-4 flex items-center gap-2 flex-wrap" data-testid="mock-time-mode-toggle">
            {TIME_MODES.map((m) => {
              const Icon = m.icon;
              const active = timeMode === m.id;
              return (
                <button
                  key={m.id}
                  onClick={() => setTimeMode(m.id)}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-sm text-[13px] font-medium transition-colors min-h-[44px] ${
                    active ? "bg-zinc-950 text-white" : "bg-white text-zinc-800 border border-zinc-300 hover:border-zinc-500"
                  }`}
                  data-testid={`mock-time-mode-${m.id}`}
                >
                  <Icon className="w-4 h-4" strokeWidth={1.5} />
                  <span>{m.label}</span>
                  <span className={`text-[11px] ${active ? "text-zinc-300" : "text-zinc-600"}`}>· {m.sub}</span>
                </button>
              );
            })}
          </div>

          {/* Account tabs (max 3 from intake — hardcoded for mock) */}
          <div className="mt-4 flex items-center gap-1 border-b border-zinc-200 -mb-px" data-testid="mock-account-tabs">
            {MOCK_ACCOUNT_TABS.map((a) => (
              <button
                key={a.id}
                onClick={() => setActiveAccount(a.id)}
                className={`px-3 py-2 text-[13px] font-medium border-b-2 transition-colors min-h-[44px] ${
                  activeAccount === a.id ? "border-amber-700 text-zinc-950" : "border-transparent text-zinc-600 hover:text-zinc-900"
                }`}
                data-testid={`mock-account-tab-${a.id}`}
              >
                {a.name}
              </button>
            ))}
          </div>

          {/* Calibration banner */}
          <div className="mt-3 px-3 py-1.5 bg-amber-50 border border-amber-200 inline-block" data-testid="mock-calibration-banner">
            <p className="text-[11px] text-amber-900 uppercase tracking-widest font-medium">
              Your Reveal is calibrated for: <span className="text-amber-800">{MOCK_CALIBRATION}</span>
            </p>
          </div>
          {/* Persona acknowledgement intentionally NOT rendered (dropped per Chunk 3). */}
        </div>
      </header>

      {/* Main */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-10">
        {/* Signal this week */}
        <section data-testid="mock-signal-this-week">
          <h2 className="text-[12px] font-semibold uppercase tracking-widest text-amber-800 mb-2">Signal this week</h2>
          <p className="text-[18px] sm:text-[20px] leading-relaxed text-zinc-950" style={{ fontFamily: "'Source Serif Pro', Georgia, serif" }}>
            {deck.signal_sentence}
          </p>
        </section>

        {/* Action cards */}
        <section data-testid="mock-actions-section">
          <h2 className="text-[12px] font-semibold uppercase tracking-widest text-zinc-700 mb-4">
            Actions this week <span className="text-zinc-500 font-normal normal-case ml-1">· {visibleCards.length} of {deck.cards.length}</span>
          </h2>
          <div className="space-y-4">
            {visibleCards.map((c) => (
              <ActionCard
                key={c.rank}
                card={c}
                expanded={!!expandedRanks[c.rank]}
                onToggle={() => handleToggleCard(c)}
                feedback={feedback[c.rank]}
                onFeedback={(state) => handleFeedback(c.rank, state)}
                sources={deck.sources}
              />
            ))}
          </div>
        </section>

        {/* Sources */}
        <section className="border-t border-zinc-200 pt-6" data-testid="mock-sources-section">
          <button
            onClick={() => setSourcesOpen((v) => !v)}
            className="w-full flex items-center justify-between text-left group min-h-[44px]"
            data-testid="mock-sources-toggle"
          >
            <h2 className="text-[12px] font-semibold uppercase tracking-widest text-zinc-700">
              Sources used ({deck.sources.length})
            </h2>
            {sourcesOpen ? <ChevronUp className="w-4 h-4 text-zinc-600" /> : <ChevronDown className="w-4 h-4 text-zinc-600" />}
          </button>

          {sourcesOpen && (
            <div className="mt-4 space-y-6" data-testid="mock-sources-expanded">
              <p className="text-[13px] italic text-zinc-700">
                These are the public signals we read this week to shape your Reveal. The half you bring is the private context inside the account.
              </p>

              <div>
                <h3 className="text-[11px] font-semibold uppercase tracking-widest text-amber-800 mb-2">
                  Used in cross-connection ({usedSources.length})
                </h3>
                <ol className="space-y-2">
                  {usedSources.map((s) => (
                    <li key={s.n} id={`source-${s.n}`} className="flex items-start gap-3 text-[13px] scroll-mt-24 px-1 py-1 rounded-sm hover:bg-amber-50/40" data-testid={`mock-source-${s.n}`}>
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-6 bg-amber-50 border border-amber-200 text-amber-800 text-[11px] font-semibold rounded-sm">
                        {s.n}
                      </span>
                      <div className="flex-1 min-w-0">
                        {s.url ? (
                          <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-zinc-950 font-medium hover:text-amber-800">{s.headline}</a>
                        ) : (
                          <span className="text-zinc-950 font-medium">{s.headline}</span>
                        )}
                        <p className="text-[12px] text-zinc-600 mt-0.5">
                          <ArrowUpRight className="inline w-3 h-3 mr-0.5 text-zinc-500" strokeWidth={1.5} />
                          {s.outlet} · {s.date}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>

              <div>
                <h3 className="text-[11px] font-semibold uppercase tracking-widest text-zinc-700 mb-2">
                  Other public signals harvested ({otherSources.length})
                </h3>
                <ol className="space-y-2">
                  {otherSources.map((s) => (
                    <li key={s.n} id={`source-${s.n}`} className="flex items-start gap-3 text-[13px] scroll-mt-24 px-1 py-1" data-testid={`mock-source-${s.n}`}>
                      <span className="flex-shrink-0 inline-flex items-center justify-center w-7 h-6 bg-zinc-100 border border-zinc-200 text-zinc-700 text-[11px] font-semibold rounded-sm">
                        {s.n}
                      </span>
                      <div className="flex-1 min-w-0">
                        <span className="text-zinc-900">{s.headline}</span>
                        <p className="text-[12px] text-zinc-600 mt-0.5">{s.outlet} · {s.date}</p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
